import { useLayoutEffect, useState } from 'react';
import {
  measureContentHeight,
  sidebarContentNeedsScroll,
  sidebarNeedsCompactActions,
} from '../utils/sidebar-actions-fit.js';

export type SidebarActionsMode = 'full' | 'compact';

export interface SidebarLayoutFit {
  actionsMode: SidebarActionsMode;
  needsContentScroll: boolean;
}

interface UseSidebarActionsFitArgs {
  enabled: boolean;
  sidebar: HTMLElement | null;
  header: HTMLElement | null;
  content: HTMLElement | null;
  footer: HTMLElement | null;
  fullActionBarItemCount: number;
  separatorCount?: number;
}

const DEFAULT_LAYOUT_FIT: SidebarLayoutFit = {
  actionsMode: 'full',
  needsContentScroll: false,
};

export function useSidebarActionsFit({
  enabled,
  sidebar,
  header,
  content,
  footer,
  fullActionBarItemCount,
  separatorCount = 0,
}: UseSidebarActionsFitArgs): SidebarLayoutFit {
  const [layoutFit, setLayoutFit] = useState<SidebarLayoutFit>(DEFAULT_LAYOUT_FIT);

  useLayoutEffect(() => {
    if (!enabled || !sidebar || !header || !content || !footer) {
      setLayoutFit(DEFAULT_LAYOUT_FIT);
      return;
    }

    const measure = (): void => {
      const contentHeight = measureContentHeight(content);
      const availableHeight = sidebar.clientHeight;
      const headerHeight = header.offsetHeight;
      const footerHeight = footer.offsetHeight;

      const needsCompact = sidebarNeedsCompactActions({
        availableHeight,
        headerHeight,
        contentHeight,
        fullActionBarItemCount,
        footerHeight,
        separatorCount,
      });

      const actionsMode: SidebarActionsMode = needsCompact ? 'compact' : 'full';
      const needsScroll = sidebarContentNeedsScroll({
        availableHeight,
        headerHeight,
        contentHeight,
        footerHeight,
        actionsMode,
        fullActionBarItemCount,
        separatorCount,
      });

      setLayoutFit({ actionsMode, needsContentScroll: needsScroll });
    };

    measure();

    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    resizeObserver?.observe(sidebar);
    resizeObserver?.observe(header);
    resizeObserver?.observe(content);
    resizeObserver?.observe(footer);

    Array.from(content.children).forEach((child) => resizeObserver?.observe(child));

    const mutationObserver = new MutationObserver(() => {
      Array.from(content.children).forEach((child) => resizeObserver?.observe(child));
      measure();
    });
    mutationObserver.observe(content, { childList: true, subtree: true, attributes: true });

    return () => {
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, [enabled, sidebar, header, content, footer, fullActionBarItemCount, separatorCount]);

  return enabled ? layoutFit : DEFAULT_LAYOUT_FIT;
}
